// src/components/VitalsWithPathologyForm.jsx
// Frontend for Service ID 34 — "Vitals with Pathology".
// Upload BMR PDF -> review/edit -> Save to PatientVitalsView (POST/PATCH /patients/<id>/vitals/).
// Reopening the patient restores: latest BMR upload record (all BMR fields) overlaid with the
// saved vitals row (what was actually persisted).

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchTechnicianPatients, submitTechnicianServiceDone } from "./api";
import {
  fetchTechnicianAssignments,
  uploadBmrPdf,
  fetchBmrRecords,
  fetchBmrRecordDetail,
  fetchPatientVitals,
  saveVitals,
} from "../utils/bmrApi";

export const VITALS_WITH_PATHOLOGY_SERVICE_ID = 34;

const CTX_STORAGE_KEY = "vwp_route_ctx";
const MAX_BMR_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

const ALLOWED_BMR_EXTENSIONS = [
  ".pdf",
  ".jpg",
  ".jpeg",
  ".png",
  ".webp",
  ".tif",
  ".tiff",
  ".bmp",
];

const ALLOWED_BMR_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/tiff",
  "image/bmp",
];

const CONFIRMED_UNITS = {};

// PatientVitalsView accepts ONLY "NEGATIVE" / "POSITIVE" for hiv, hcv, hbsag.
// Set to true only after the backend validation also allows INCONCLUSIVE.
const BACKEND_ALLOWS_INCONCLUSIVE = false;
const SEROLOGY_OPTIONS = [
  { value: "", label: "Not selected" },
  { value: "negative", label: "Negative" },
  { value: "positive", label: "Positive" },
  ...(BACKEND_ALLOWS_INCONCLUSIVE ? [{ value: "inconclusive", label: "Inconclusive" }] : []),
];
const SEROLOGY_ALLOWED = new Set(SEROLOGY_OPTIONS.map((o) => o.value).filter(Boolean));

// Form field key -> key accepted by PatientVitalsView (VITALS_FIELDS).
// Fields NOT listed here are NOT stored by that view (it silently ignores them).
const VITALS_KEY = {
  height: "height",
  weight: "weight",
  body_fat: "body_fat",
  muscle_mass: "muscle_mass",
  muscle_rate: "muscle_rate",
  skeletal_muscle: "skeletal_muscle",
  bone_mass: "bone_mass",
  protein_mass: "protein_mass",
  protein: "protein_rate",
  bmr: "bmr",
  blood_glucose: "blood_glucose",
  hba1c: "hba1c",
  hiv: "hiv",
  hcv: "hcv",
  hbsag: "hbsag",
  hemoglobin: "hemoglobin",
  // visceral_fat: "visceral_rate",  // enable ONLY if both mean the same measurement
};

const FIELDS = [
  { key: "height", label: "Height", aliases: ["height"], group: "bmr", type: "number" },
  { key: "weight", label: "Weight", aliases: ["weight"], group: "bmr", type: "number" },
  { key: "bmi", label: "BMI", aliases: ["bmi"], group: "bmr", type: "number" },
  { key: "body_fat", label: "Body Fat", aliases: ["body_fat", "bodyfat", "body_fat_percentage"], group: "bmr", type: "number" },
  { key: "fat_mass", label: "Fat Mass", aliases: ["fat_mass"], group: "bmr", type: "number" },
  { key: "fat_free_body_weight", label: "Fat-free Body Weight", aliases: ["fat_free_body_weight", "fat_free_weight", "lean_body_weight"], group: "bmr", type: "number" },
  { key: "muscle_mass", label: "Muscle Mass", aliases: ["muscle_mass"], group: "bmr", type: "number" },
  { key: "muscle_rate", label: "Muscle Rate", aliases: ["muscle_rate"], group: "bmr", type: "number" },
  { key: "skeletal_muscle", label: "Skeletal Muscle", aliases: ["skeletal_muscle"], group: "bmr", type: "number" },
  { key: "bone_mass", label: "Bone Mass", aliases: ["bone_mass"], group: "bmr", type: "number" },
  { key: "protein_mass", label: "Protein Mass", aliases: ["protein_mass"], group: "bmr", type: "number" },
  { key: "protein", label: "Protein", aliases: ["protein", "protein_percentage", "protein_rate"], group: "bmr", type: "number" },
  { key: "water_weight", label: "Water Weight", aliases: ["water_weight"], group: "bmr", type: "number" },
  { key: "body_water", label: "Body Water", aliases: ["body_water", "body_water_percentage"], group: "bmr", type: "number" },
  { key: "subcutaneous_fat", label: "Subcutaneous Fat", aliases: ["subcutaneous_fat"], group: "bmr", type: "number" },
  { key: "visceral_fat", label: "Visceral Fat", aliases: ["visceral_fat"], group: "bmr", type: "number" },
  { key: "bmr", label: "BMR", aliases: ["bmr"], group: "bmr", type: "number" },
  { key: "body_age", label: "Body Age", aliases: ["body_age"], group: "bmr", type: "number" },
  { key: "ideal_body_weight", label: "Ideal Body Weight", aliases: ["ideal_body_weight", "ideal_weight"], group: "bmr", type: "number" },

  { key: "blood_glucose", label: "Blood Glucose", aliases: ["blood_glucose"], group: "clinical", type: "number" },
  { key: "hba1c", label: "HbA1c", aliases: ["hba1c"], group: "clinical", type: "number" },
  { key: "hemoglobin", label: "Hemoglobin", aliases: ["hemoglobin", "haemoglobin", "hb"], group: "clinical", type: "number" },
  { key: "hiv", label: "HIV", aliases: ["hiv"], group: "clinical", type: "serology" },
  { key: "hcv", label: "HCV", aliases: ["hcv"], group: "clinical", type: "serology" },
  { key: "hbsag", label: "HBsAg", aliases: ["hbsag"], group: "clinical", type: "serology" },
];

const BMR_FIELDS = FIELDS.filter((f) => f.group === "bmr");
const CLINICAL_FIELDS = FIELDS.filter((f) => f.group === "clinical");
const STORED_FIELDS = FIELDS.filter((f) => VITALS_KEY[f.key]);
const NOT_STORED_FIELDS = FIELDS.filter((f) => !VITALS_KEY[f.key]);
const emptyValues = () => Object.fromEntries(FIELDS.map((f) => [f.key, ""]));

// ---------- helpers ----------
const toId = (v) => (v === undefined || v === null || v === "" ? null : v);
const sameNum = (a, b) => Number(a) === Number(b);

const readStoredCtx = () => {
  try {
    return JSON.parse(sessionStorage.getItem(CTX_STORAGE_KEY) || "null");
  } catch {
    return null;
  }
};

const pickRaw = (obj, aliases) => {
  if (!obj || typeof obj !== "object") return undefined;
  for (const a of aliases) {
    if (obj[a] !== undefined && obj[a] !== null && obj[a] !== "") return obj[a];
  }
  return undefined;
};

// Accepts NEGATIVE/POSITIVE (any case); anything else is reported, never guessed.
const normalizeSerology = (raw) => {
  const s = String(raw).trim().toLowerCase();
  return ["negative", "positive", "inconclusive"].includes(s) ? s : null;
};

const extractParsed = (resp) => {
  const root =
    resp?.record ||
    resp?.data?.record ||
    resp?.data?.parsed_data ||
    resp?.data?.parsed ||
    resp?.data?.parameters ||
    resp?.data?.values ||
    resp?.parsed_data ||
    resp?.parsed ||
    resp?.parameters ||
    resp?.values ||
    resp?.data ||
    resp ||
    {};

  const values = emptyValues();
  const detected = {};
  const unrecognized = {};
  const units = { ...(resp?.units || {}), ...(root?.units || {}) };

  FIELDS.forEach((field) => {
    let raw = pickRaw(root, field.aliases);
    if (raw && typeof raw === "object") {
      if (raw.unit) units[field.key] = raw.unit;
      raw = raw.value;
    }
    if (raw === undefined || raw === null || String(raw).trim() === "") return;

    if (field.type === "serology") {
      const norm = normalizeSerology(raw);
      if (norm) {
        values[field.key] = norm;
        detected[field.key] = true;
      } else {
        unrecognized[field.key] = String(raw);
      }
      return;
    }
    values[field.key] = String(raw);
    detected[field.key] = true;
  });

  return { values, detected, units, unrecognized };
};

const normalizeRecordList = (data) => {
  const list = Array.isArray(data) ? data : data?.records || data?.results || data?.data || [];
  return Array.isArray(list) ? list : [];
};
const unwrapRecord = (data) => data?.record || data?.data?.record || data?.data || data;

const recordId = (r) => r?.id ?? r?.record_id ?? r?.pk;
const recordDate = (r) => r?.report_date || r?.created_at || r?.date || r?.created || null;
const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? String(d) : dt.toLocaleString();
};
const fmtSize = (b) => (b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / (1024 * 1024)).toFixed(2)} MB`);
const unitFor = (key, units) => units?.[key] || CONFIRMED_UNITS[key] || "";

// BMR upload records: every identifier a record carries must match the current context.
const isApplicableBmrRecord = (r, ctx) => {
  const pid = r?.patient_unique_id ?? r?.patient_id;
  if (toId(pid) !== null && String(pid) !== String(ctx.patientId) && String(pid) !== String(ctx.patientDbId)) return false;
  if (toId(r?.camp_id) !== null && !sameNum(r.camp_id, ctx.campId)) return false;
  if (toId(r?.assignment_id) !== null && !sameNum(r.assignment_id, ctx.assignmentId)) return false;
  if (toId(r?.service_id) !== null && !sameNum(r.service_id, VITALS_WITH_PATHOLOGY_SERVICE_ID)) return false;
  return true;
};

const pickLatest = (list) => {
  if (list.length === 0) return null;
  if (list.length === 1) return list[0];
  const times = list.map((r) => Date.parse(recordDate(r) || ""));
  if (times.every((t) => !Number.isNaN(t))) {
    let best = 0;
    times.forEach((t, i) => {
      if (t > times[best]) best = i;
    });
    return list[best];
  }
  const ids = list.map((r) => Number(recordId(r)));
  if (ids.every((n) => Number.isFinite(n))) {
    let best = 0;
    ids.forEach((n, i) => {
      if (n > ids[best]) best = i;
    });
    return list[best];
  }
  return null;
};

const Alert = ({ kind, children, onDismiss }) => {
  const styles = {
    error: "bg-red-50 border-red-300 text-red-800",
    success: "bg-green-50 border-green-300 text-green-800",
    info: "bg-blue-50 border-blue-300 text-blue-800",
    warn: "bg-yellow-50 border-yellow-300 text-yellow-800",
  };
  return (
    <div role={kind === "error" ? "alert" : "status"} className={`border rounded-lg px-4 py-3 text-sm flex items-start justify-between gap-3 ${styles[kind]}`}>
      <div>{children}</div>
      {onDismiss && (
        <button type="button" onClick={onDismiss} className="font-medium hover:underline shrink-0">
          Dismiss
        </button>
      )}
    </div>
  );
};

const Section = ({ title, subtitle, children }) => (
  <section className="bg-white border border-gray-300 shadow rounded-lg mb-6">
    <div className="px-5 py-4 border-b bg-blue-50 rounded-t-lg">
      <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
      {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

// ---------- component ----------
function VitalsWithPathologyForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const routeState = location.state;

  const [ctx, setCtx] = useState(null);
  const [ctxStatus, setCtxStatus] = useState("loading");
  const [ctxError, setCtxError] = useState("");

  // upload
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [uploadStatus, setUploadStatus] = useState("idle");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef(null);

  // review form
  const [reviewReady, setReviewReady] = useState(false);
  const [valuesSource, setValuesSource] = useState(null); // "upload" | "saved" | null
  const [values, setValues] = useState(emptyValues());
  const [detected, setDetected] = useState({});
  const [units, setUnits] = useState({});
  const [unrecognized, setUnrecognized] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  // persistence state
  const [vitalsExists, setVitalsExists] = useState(null); // true | false | null(unknown)
  const [vitalsSnapshot, setVitalsSnapshot] = useState({}); // form keys that currently have a value in the DB
  const [savedVitalsId, setSavedVitalsId] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [bmrRecordId, setBmrRecordId] = useState(null);
  const [sourceFileName, setSourceFileName] = useState(null);
  const [serviceCompleted, setServiceCompleted] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const redirectTimerRef = useRef(null);

  // lookup
  const [lookupStatus, setLookupStatus] = useState("idle");
  const [lookupError, setLookupError] = useState("");
  const [loadNotice, setLoadNotice] = useState("");

  // save
  const [saveStatus, setSaveStatus] = useState("idle");
  const [saveMessage, setSaveMessage] = useState("");

  const sessionRef = useRef(0);
  const dirtyRef = useRef(false);
  const savingRef = useRef(false);

  // ---------- 1. Resolve context ----------
  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      setCtxStatus("loading");
      setCtxError("");

      const base = routeState && (routeState.patientId || routeState.technicianId) ? routeState : readStoredCtx();
      if (!base || !base.patientId || !base.technicianId) {
        setCtxStatus("blocked");
        setCtxError("Patient details are missing. Please open this service again from the patient dashboard.");
        return;
      }
      if (base.serviceId !== undefined && base.serviceId !== null && !sameNum(base.serviceId, VITALS_WITH_PATHOLOGY_SERVICE_ID)) {
        setCtxStatus("blocked");
        setCtxError("This screen is only available for the Vitals with Pathology service.");
        return;
      }

      try {
        const assignments = await fetchTechnicianAssignments(base.technicianId);
        const assignment = assignments.find(
          (a) =>
            sameNum(a.service_id, VITALS_WITH_PATHOLOGY_SERVICE_ID) &&
            (toId(base.campId) === null || sameNum(a.camp_id, base.campId)) &&
            (toId(base.assignmentId) === null || sameNum(a.assignment_id, base.assignmentId))
        );
        if (cancelled) return;
        if (!assignment) {
          setCtxStatus("blocked");
          setCtxError("Vitals with Pathology is not assigned to this technician for this camp.");
          return;
        }
        if (assignment.is_enabled !== true) {
          setCtxStatus("blocked");
          setCtxError("This service is currently disabled for this camp.");
          return;
        }

        let patientName = base.patientName;
        if (!patientName && base.packageId) {
          try {
            const list = await fetchTechnicianPatients({
              technicianId: base.technicianId,
              packageId: base.packageId,
              campId: assignment.camp_id,
            });
            const match = (list || []).find((p) => p.unique_patient_id === base.patientId);
            patientName = match?.name;
          } catch {
            /* cosmetic */
          }
        }

        const resolved = {
          patientId: String(base.patientId).trim(), // patient_unique_id
          patientDbId: toId(base.patientDbId), // keep for compatibility/display
          patientName: patientName || "",
          technicianId: base.technicianId,
          packageId: base.packageId ?? null,
          packageName: base.packageName ?? null,
          isEditing: !!base.isEditing,
          assignmentId: assignment.assignment_id,
          campId: assignment.camp_id,
          campLocation: assignment.camp_location,
          serviceId: assignment.service_id,
          serviceName: assignment.service_name || "Vitals with Pathology",
          isEnabled: assignment.is_enabled,
        };
        sessionStorage.setItem(CTX_STORAGE_KEY, JSON.stringify(resolved));
        setCtx(resolved);
        setCtxStatus("ready");
      } catch (err) {
        if (cancelled) return;
        setCtxStatus("blocked");
        setCtxError(err.message || "Could not verify the service assignment.");
      }
    };
    resolve();
    return () => {
      cancelled = true;
    };
  }, [routeState]);

  // ---------- 2. Restore saved data ----------
  const patientKey = ctx ? `${ctx.patientId}|${ctx.assignmentId}|${ctx.patientDbId ?? ""}` : null;

  // Latest applicable BMR upload record (full detail), or null.
  const lookupBmrRecord = useCallback(async () => {
    const data = await fetchBmrRecords({
      patient_unique_id: ctx.patientId,
      camp_id: ctx.campId,
      assignment_id: ctx.assignmentId,
      service_id: ctx.serviceId,
    });
    const list = normalizeRecordList(data).filter((r) => isApplicableBmrRecord(r, ctx));
    if (list.length === 0) return { full: null, notice: "" };
    const latest = pickLatest(list);
    if (!latest) return { full: null, notice: "Several BMR upload records were found but none has a reliable date or ID, so none was loaded." };
    const id = recordId(latest);
    if (id === undefined || id === null) return { full: null, notice: "The BMR upload record has no ID, so it was not loaded." };
    const detail = unwrapRecord(await fetchBmrRecordDetail(id));
    if (!detail || typeof detail !== "object" || !isApplicableBmrRecord(detail, ctx)) {
      return { full: null, notice: "The BMR upload record does not match this patient / camp / assignment, so it was not loaded." };
    }
    return { full: { ...detail, __id: recordId(detail) ?? id }, notice: "" };
  }, [ctx]);

  const lookupSaved = useCallback(async () => {
    if (!ctx) return;
    const session = sessionRef.current;
    setLookupStatus("loading");
    setLookupError("");

    const [vRes, bRes] = await Promise.allSettled([
      ctx.patientId
        ? fetchPatientVitals(ctx.patientId)
        : Promise.resolve(undefined),
      lookupBmrRecord(),
    ]);
    if (session !== sessionRef.current) return;

    const errors = [];
    let vitalsRec = null;
    if (vRes.status === "fulfilled") {
      if (vRes.value !== undefined) {
        vitalsRec = vRes.value?.record ?? null;
        // record.patient_id must match the integer id we asked for
        // Validate against the unique patient ID returned by the API.
        const returnedPatientId =
          vitalsRec?.patient_unique_id ??
          vitalsRec?.patient?.patient_unique_id;

        if (
          vitalsRec &&
          returnedPatientId != null &&
          String(returnedPatientId) !== String(ctx.patientId)
        ) {
          vitalsRec = null;
          errors.push("Saved vitals did not match this patient and were ignored.");
        }
        setVitalsExists(!!vitalsRec);
      } else {
        setVitalsExists(null);
      }
    } else {
      setVitalsExists(null);
      errors.push(`Could not load saved vitals: ${vRes.reason?.message || "unknown error"}`);
    }

    let bmrFull = null;
    if (bRes.status === "fulfilled") {
      bmrFull = bRes.value.full;
      if (bRes.value.notice) setLoadNotice(bRes.value.notice);
    } else {
      errors.push(`Could not load the BMR upload record: ${bRes.reason?.message || "unknown error"}`);
    }

    const parsedV = vitalsRec ? extractParsed(vitalsRec) : null;
    setVitalsSnapshot(parsedV ? parsedV.detected : {});
    setSavedVitalsId(vitalsRec ? recordId(vitalsRec) ?? null : null);
    setSavedAt(vitalsRec ? recordDate(vitalsRec) : null);

    const hasVitalsData = parsedV && Object.keys(parsedV.detected).length > 0;
    if ((bmrFull || hasVitalsData) && !dirtyRef.current) {
      const base = bmrFull ? extractParsed(bmrFull) : null;
      const merged = base ? { ...base.values } : emptyValues();
      const det = base ? { ...base.detected } : {};
      const unr = base ? { ...base.unrecognized } : {};
      if (parsedV) {
        Object.keys(parsedV.detected).forEach((k) => {
          merged[k] = parsedV.values[k]; // persisted values win
          det[k] = true;
          delete unr[k];
        });
        Object.assign(unr, parsedV.unrecognized);
      }
      setValues(merged);
      setDetected(det);
      setUnits(base ? base.units : {});
      setUnrecognized(unr);
      setFieldErrors({});
      setBmrRecordId(bmrFull ? bmrFull.__id : null);
      setSourceFileName(bmrFull ? bmrFull.source_file_name || bmrFull.file_name || bmrFull.filename || null : null);
      setValuesSource("saved");
      setReviewReady(true);
    }

    if (errors.length) {
      setLookupError(errors.join(" "));
      setLookupStatus("error");
    } else {
      setLookupStatus("ready");
    }
  }, [ctx, lookupBmrRecord]);

  // Reset on patient/assignment change, then restore.
  useEffect(() => {
    sessionRef.current += 1;
    dirtyRef.current = false;
    savingRef.current = false;
    setFile(null);
    setFileError("");
    setUploadStatus("idle");
    setUploadProgress(0);
    setUploadError("");
    setReviewReady(false);
    setValuesSource(null);
    setValues(emptyValues());
    setDetected({});
    setUnits({});
    setUnrecognized({});
    setFieldErrors({});
    setVitalsExists(null);
    setVitalsSnapshot({});
    setSavedVitalsId(null);
    setSavedAt(null);
    setBmrRecordId(null);
    setSourceFileName(null);
    setLookupStatus("idle");
    setLookupError("");
    setLoadNotice("");
    setSaveStatus("idle");
    setSaveMessage("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (patientKey) lookupSaved();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientKey]);

  // Clear a pending redirect if the component unmounts first
  useEffect(
    () => () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    },
    []
  );

  // Back to the patient list. The dashboard needs this state or it shows an error.
  const goToDashboard = useCallback(() => {
    if (ctx?.packageId && ctx?.technicianId) {
      navigate("/patient-dashboard", {
        state: {
          packageId: ctx.packageId,
          technicianId: ctx.technicianId,
          campId: ctx.campId,
          packageName: ctx.packageName,
        },
      });
    } else {
      navigate("/technical-dashboard"); // fallback if the package info is missing
    }
  }, [navigate, ctx]);

  // Short pause so the technician sees the success message, then redirect
  const redirectAfterSave = () => {
    setRedirecting(true);
    redirectTimerRef.current = setTimeout(goToDashboard, 1200);
  };

  // ---------- Completion status (authoritative: patient list API) ----------
  useEffect(() => {
    if (!ctx) return undefined;
    let cancelled = false;
    setServiceCompleted(null);

    (async () => {
      let status = null;
      if (ctx.packageId) {
        try {
          const rows = await fetchTechnicianPatients({
            technicianId: ctx.technicianId,
            packageId: ctx.packageId,
            campId: ctx.campId,
          });
          const wanted = String(ctx.serviceName || "Vitals with Pathology").trim().toLowerCase();
          const row = (rows || []).find(
            (r) =>
              String(r.unique_patient_id) === String(ctx.patientId) &&
              String(r.services || "").trim().toLowerCase() === wanted
          );
          if (row && typeof row.completed === "boolean") status = row.completed;
        } catch {
          /* fall back below */
        }
      }
      // Fallback only if the list could not be read. The dashboard sets isEditing from
      // this same backend `completed` flag when it opens the page.
      if (status === null) status = !!ctx.isEditing;
      if (!cancelled) setServiceCompleted(status);
    })();

    return () => {
      cancelled = true;
    };
  }, [patientKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];

    setUploadError("");
    setFileError("");
    setUploadStatus("idle");

    if (!f) {
      setFile(null);
      return;
    }

    const fileName = f.name.toLowerCase();
    const hasAllowedExtension = ALLOWED_BMR_EXTENSIONS.some(
      (ext) => fileName.endsWith(ext)
    );

    // Some browsers return an empty or inconsistent MIME type.
    // The extension is also checked to support those cases.
    const hasAllowedMimeType = ALLOWED_BMR_MIME_TYPES.includes(f.type);

    if (!hasAllowedExtension && !hasAllowedMimeType) {
      setFile(null);
      setFileError(
        "Unsupported file type. Upload a PDF, JPG, JPEG, PNG, WEBP, TIF, TIFF, or BMP file."
      );
      e.target.value = "";
      return;
    }

    if (f.size === 0) {
      setFile(null);
      setFileError("This file is empty.");
      e.target.value = "";
      return;
    }

    if (f.size > MAX_BMR_FILE_BYTES) {
      setFile(null);
      setFileError(
        `File is too large (${fmtSize(f.size)}). Maximum size is ${fmtSize(
          MAX_BMR_FILE_BYTES
        )}.`
      );
      e.target.value = "";
      return;
    }

    setFileError("");
    setFile(f);
  };

  const removeFile = () => {
    setFile(null);
    setFileError("");
    setUploadStatus("idle");
    setUploadError("");
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpload = async () => {
    if (!file || !ctx || uploadStatus === "uploading" || uploadStatus === "parsing") return;
    const session = sessionRef.current;
    setUploadStatus("uploading");
    setUploadProgress(0);
    setUploadError("");
    try {
      const resp = await uploadBmrPdf({
        file,
        fields: {
          patient_unique_id: ctx.patientId,
          camp_id: ctx.campId,
          assignment_id: ctx.assignmentId,
          service_id: ctx.serviceId,
          technician_id: ctx.technicianId,
        },
        onProgress: (p) => session === sessionRef.current && setUploadProgress(p),
        onUploadComplete: () => session === sessionRef.current && setUploadStatus("parsing"),
      });
      if (session !== sessionRef.current) return;
      const parsed = extractParsed(resp);

      // Extracted values replace matching ones; anything the PDF did not return is kept.
      setValues((prev) => {
        const next = { ...prev };
        Object.keys(parsed.detected).forEach((k) => {
          next[k] = parsed.values[k];
        });
        return next;
      });
      setDetected(parsed.detected);
      setUnits((prev) => ({ ...prev, ...parsed.units }));
      setUnrecognized(parsed.unrecognized);
      setFieldErrors({});
      setValuesSource("upload");
      setReviewReady(true);
      setSourceFileName(file.name);
      const uploadedId = recordId(unwrapRecord(resp)) ?? recordId(resp);
      if (uploadedId !== undefined && uploadedId !== null) setBmrRecordId(uploadedId);

      dirtyRef.current = true;
      setSaveStatus("idle");
      setSaveMessage("");
      setUploadStatus("success");
    } catch (err) {
      if (session !== sessionRef.current) return;
      setUploadStatus("error"); // existing values untouched
      setUploadError(err.message);
    }
  };

  // ---------- Form ----------
  const handleValueChange = (key, v) => {
    dirtyRef.current = true;
    setValues((prev) => ({ ...prev, [key]: v }));
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
    setUnrecognized((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
    if (saveStatus !== "saving") {
      setSaveStatus("idle");
      setSaveMessage("");
    }
  };

  // Validation mirrors validate_vitals_data in the backend for the fields we send.
  const validate = () => {
    const errs = {};
    let anyStored = false;
    FIELDS.forEach((f) => {
      const v = String(values[f.key] ?? "").trim();
      if (v === "") return;
      if (VITALS_KEY[f.key]) anyStored = true;
      if (f.type === "serology") {
        if (!SEROLOGY_ALLOWED.has(v)) errs[f.key] = "Choose Negative or Positive.";
      } else if (!/^\d+(\.\d+)?$/.test(v)) {
        errs[f.key] = "Enter a valid non-negative number.";
      } else if ((f.key === "height" || f.key === "weight") && Number(v) === 0) {
        errs[f.key] = "Value must be greater than zero.";
      }
    });
    const snapshotHasStored = STORED_FIELDS.some((f) => vitalsSnapshot[f.key]);
    if (!anyStored && !snapshotHasStored) {
      errs._form = "Enter at least one value that the patient vitals record can store before saving.";
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Calls the existing completion API. Does NOT change serviceCompleted by itself.
  const completeService = async () => {
    try {
      await submitTechnicianServiceDone({
        patientId: ctx.patientId,
        technicianId: ctx.technicianId,
        serviceId: VITALS_WITH_PATHOLOGY_SERVICE_ID, // 34
      });
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err?.message || "Unknown error" };
    }
  };

  // For when vitals are already saved but completion failed. Does not re-save vitals.
  const handleRetryCompletion = async () => {
    if (savingRef.current || !ctx || serviceCompleted === true) return;
    savingRef.current = true;
    const session = sessionRef.current;
    setSaveStatus("saving");
    setSaveMessage("");
    const r = await completeService();
    savingRef.current = false;
    if (session !== sessionRef.current) return;
    if (r.ok) {
      setServiceCompleted(true);
      setSaveStatus("success");
      setSaveMessage("Service marked completed. Returning to the patient list…");
      redirectAfterSave();
    } else {
      setSaveStatus("partial");
      setSaveMessage(`Vitals are saved, but marking the service completed failed: ${r.error}`);
    }
  };

  const handleSave = async () => {
    if (savingRef.current || saveStatus === "saving" || !ctx) return;
    if (!ctx.patientId || !String(ctx.patientId).trim()) {
      setSaveStatus("error");
      setSaveMessage(
        "The unique patient ID is missing, so vitals cannot be saved. Reopen this service from the patient dashboard."
      );
      return;
    }
    if (!validate()) {
      setSaveStatus("idle");
      setSaveMessage("");
      return;
    }
    savingRef.current = true;
    const session = sessionRef.current;
    setSaveStatus("saving");
    setSaveMessage("");

    // Only keys the backend stores. A blank is sent as null ONLY if that field currently has a
    // saved value (i.e. the technician cleared it); otherwise it is omitted so nothing is erased.
    const payload = {};
    STORED_FIELDS.forEach((f) => {
      const v = String(values[f.key] ?? "").trim();
      const vk = VITALS_KEY[f.key];
      if (v === "") {
        if (vitalsSnapshot[f.key]) payload[vk] = null;
      } else if (f.type === "serology") {
        payload[vk] = v.toUpperCase(); // "NEGATIVE" | "POSITIVE"
      } else {
        payload[vk] = Number(v);
      }
    });

    try {
      const resp = await saveVitals({patientUniqueId: ctx.patientId, payload, exists: vitalsExists,});
      if (session !== sessionRef.current) return;
      if (!resp || resp.success !== true || !resp.record) {
        throw new Error(resp?.error || resp?.message || "The server did not confirm the save.");
      }

      // Reflect what the database now holds for the stored fields.
      const parsed = extractParsed(resp.record);
      setValues((prev) => {
        const next = { ...prev };
        STORED_FIELDS.forEach((f) => {
          next[f.key] = parsed.values[f.key];
        });
        return next;
      });
      setUnrecognized((prev) => {
        const next = { ...prev };
        STORED_FIELDS.forEach((f) => delete next[f.key]);
        return next;
      });
      setVitalsSnapshot(parsed.detected);
      setVitalsExists(true);
      setSavedVitalsId(recordId(resp.record) ?? null);
      setSavedAt(recordDate(resp.record) || new Date().toISOString());
      setValuesSource("saved");
      setReviewReady(true);
      dirtyRef.current = false;
      // ---- NEW: only now (vitals confirmed saved) call the completion API ----
      const alreadyCompleted = serviceCompleted === true;
      let completionError = "";
      if (!alreadyCompleted) {
        const r = await completeService();
        if (session !== sessionRef.current) return;
        if (r.ok) setServiceCompleted(true);
        else completionError = r.error;
      }

      if (completionError) {
        setSaveStatus("partial");
        setSaveMessage(`Vitals were saved, but marking the service completed failed: ${completionError}`);
      } else {
        setSaveStatus("success");
        setSaveMessage(
          (alreadyCompleted
            ? resp.message || "Vitals saved successfully."
            : "Vitals saved successfully. Service marked completed.") +
            " Returning to the patient list…"
        );
        redirectAfterSave();
      }
    } catch (err) {
      if (session !== sessionRef.current) return;
      setSaveStatus("error"); // values kept for retry
      setSaveMessage(err.message);
    } finally {
      savingRef.current = false;
    }
  };

  // ---------- Render ----------
  if (ctxStatus === "loading") {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <p className="text-gray-600 text-lg">Loading Vitals with Pathology…</p>
      </div>
    );
  }

  if (ctxStatus === "blocked") {
    return (
      <div className="flex items-center justify-center min-h-screen bg-red-50 px-4">
        <div className="bg-white border border-red-300 p-6 rounded shadow max-w-md">
          <p className="text-red-800 font-semibold mb-2">Cannot open this service</p>
          <p className="text-red-600 mb-4">{ctxError}</p>
          <button onClick={() => navigate("/technical-dashboard")} className="bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-800">
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  const busyUpload = uploadStatus === "uploading" || uploadStatus === "parsing";
  const saving = saveStatus === "saving";
  const inputCls = (hasErr) =>
    `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 ${
      hasErr ? "border-red-400" : "border-gray-300"
    }`;

  const renderField = (f) => {
    const unit = f.type === "number" ? unitFor(f.key, units) : "";
    const id = `bmr-${f.key}`;
    const err = fieldErrors[f.key];
    const empty = String(values[f.key] ?? "").trim() === "";
    const notDetected = f.group === "bmr" && valuesSource === "upload" && !detected[f.key] && empty && !err;
    const notStored = !Object.prototype.hasOwnProperty.call(
      VITALS_KEY,
      f.key
    );
    return (
      <div key={f.key}>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
          {f.label}
          {unit ? ` (${unit})` : ""}
        </label>
        {f.type === "serology" ? (
          <select
            id={id}
            value={values[f.key]}
            onChange={(e) => handleValueChange(f.key, e.target.value)}
            disabled={saving}
            aria-invalid={!!err}
            aria-describedby={err ? `${id}-err` : undefined}
            className={inputCls(!!err)}
          >
            {SEROLOGY_OPTIONS.map((o) => (
              <option key={o.value || "none"} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            id={id}
            type="text"
            inputMode="decimal"
            value={values[f.key]}
            onChange={(e) => handleValueChange(f.key, e.target.value)}
            disabled={saving}
            aria-invalid={!!err}
            aria-describedby={err ? `${id}-err` : undefined}
            placeholder={f.group === "bmr" && valuesSource === "upload" && !detected[f.key] ? "Not detected" : ""}
            className={inputCls(!!err)}
          />
        )}
        {err && <p id={`${id}-err`} className="text-xs text-red-600 mt-1">{err}</p>}
        {unrecognized[f.key] && !err && (
          <p className="text-xs text-yellow-700 mt-1">
            The server returned “{unrecognized[f.key]}”, which is not a recognised option. Please choose a value.
          </p>
        )}
        {notDetected && <p className="text-xs text-yellow-700 mt-1">Not detected in the uploaded report.</p>}
        {/* {notStored && <p className="text-xs text-gray-500 mt-1">Review only — not stored by the vitals API.</p>} */}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6 gap-2">
          <h1 className="text-2xl font-bold text-gray-800">Vitals with Pathology</h1>
          <button onClick={() => navigate(-1)} className="bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-800">
            ← Back
          </button>
        </div>

        <Section title="Patient and camp information">
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div><dt className="text-gray-500">Patient name</dt><dd className="font-medium text-gray-900">{ctx.patientName || "—"}</dd></div>
            <div><dt className="text-gray-500">Patient ID</dt><dd className="font-mono text-gray-900">{ctx.patientId}</dd></div>
            <div><dt className="text-gray-500">Patient DB ID (vitals)</dt><dd className="font-mono text-gray-900">{ctx.patientDbId ?? "—"}</dd></div>
            <div><dt className="text-gray-500">Service</dt><dd className="font-medium text-gray-900">{ctx.serviceName}</dd></div>
            <div><dt className="text-gray-500">Camp</dt><dd className="font-medium text-gray-900">#{ctx.campId}{ctx.campLocation ? ` — ${ctx.campLocation}` : ""}</dd></div>
            <div><dt className="text-gray-500">Assignment ID</dt><dd className="text-gray-900">{ctx.assignmentId ?? "—"}</dd></div>
            <div>
              <dt className="text-gray-500">Status</dt>
              <dd>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${
                  serviceCompleted === true
                    ? "bg-green-100 text-green-800 border-green-200"
                    : serviceCompleted === null
                      ? "bg-gray-100 text-gray-700 border-gray-200"
                      : "bg-yellow-100 text-yellow-800 border-yellow-200"
                }`}>
                  {serviceCompleted === true ? "Completed" : serviceCompleted === null ? "Checking…" : "Pending"}
                </span>
              </dd>
            </div>
          </dl>
        </Section>

        <Section
          title="BMR / body-composition PDF"
          subtitle={
            valuesSource === "saved"
              ? "Saved data is loaded below. Uploading a new PDF overwrites matching values for you to review."
              : "Upload the device report. Values are extracted for you to review before saving."
          }
        >
          <div className="space-y-4">
            {lookupStatus === "loading" && <p className="text-sm text-gray-600">Checking for previously saved data…</p>}
            {lookupStatus === "error" && (
              <Alert kind="error">
                {lookupError}{" "}
                <button type="button" onClick={lookupSaved} className="underline font-medium">Try again</button>
              </Alert>
            )}
            {loadNotice && <Alert kind="warn" onDismiss={() => setLoadNotice("")}>{loadNotice}</Alert>}

            <div>
              <label
                htmlFor="bmr-pdf"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Choose BMR report (PDF or image)
              </label>
              <input
                id="bmr-pdf"
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.tif,.tiff,.bmp,application/pdf,image/jpeg,image/png,image/webp,image/tiff,image/bmp"
                onChange={handleFileChange}
                disabled={busyUpload}
                className="block w-full text-sm text-gray-700 border border-gray-300 rounded-lg cursor-pointer bg-white file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-blue-100 file:text-blue-900 hover:file:bg-blue-200 disabled:opacity-50"
              />
              <p className="text-xs text-gray-500 mt-1">
                Supported formats: PDF, JPG, JPEG, PNG, WEBP, TIF, TIFF, BMP.
                Maximum file size: {fmtSize(MAX_BMR_FILE_BYTES)}.
              </p>
              {fileError && <p role="alert" className="text-sm text-red-600 mt-1">{fileError}</p>}
            </div>

            {file && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">{file.name}</p>
                  <p className="text-xs text-gray-500">{fmtSize(file.size)}</p>
                </div>
                <button type="button" onClick={removeFile} disabled={busyUpload} className="text-sm text-red-600 font-medium hover:underline disabled:opacity-50 self-start sm:self-auto">
                  Remove file
                </button>
              </div>
            )}

            {busyUpload && (
              <div aria-live="polite">
                <p className="text-sm text-gray-700">
                  {uploadStatus === "uploading" ? `Uploading… ${uploadProgress}%` : "Upload complete. Reading values from the PDF…"}
                </p>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div
                    className={`bg-blue-600 h-2 rounded-full transition-all duration-300 ${uploadStatus === "parsing" ? "animate-pulse" : ""}`}
                    style={{ width: `${uploadStatus === "parsing" ? 100 : uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {uploadStatus === "error" && <Alert kind="error">{uploadError} Your existing form values were not changed.</Alert>}
            {uploadStatus === "success" && (
              <Alert kind="success">
                BMR report processed successfully. Review the extracted values below
                and add the clinical fields. Nothing is saved to the patient's vitals
                until you click Save.
              </Alert>
            )}

            <button
              type="button"
              onClick={handleUpload}
              disabled={!file || busyUpload}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busyUpload
                ? "Processing…"
                : uploadStatus === "error"
                  ? "Retry upload"
                  : "Upload and read report"}
            </button>
          </div>
        </Section>

        {reviewReady && (
          <Section
            title="Review report values"
            subtitle="Check each value against the PDF. Edit anything that looks wrong. Blank fields stay empty — nothing is filled in for you."
          >
            <div className="space-y-6">
              {valuesSource === "saved" && (savedVitalsId !== null || bmrRecordId !== null) && (
                <Alert kind="info">
                  Saved data loaded
                  {savedVitalsId !== null ? ` — patient vitals #${savedVitalsId}${savedAt ? ` (${fmtDate(savedAt)})` : ""}` : ""}
                  {bmrRecordId !== null ? `${savedVitalsId !== null ? "," : " —"} BMR upload #${bmrRecordId}` : ""}
                  {sourceFileName ? ` (${sourceFileName})` : ""}. Edit and save to update the patient vitals.
                </Alert>
              )}

              {/* <Alert kind="warn">
                Saving writes to the patient's single vitals record, which the general Vitals form also uses. These fields
                are shown for review but are <strong>not stored</strong> by the vitals API:{" "}
                {NOT_STORED_FIELDS.map((f) => f.label).join(", ")}.
              </Alert> */}

              {!ctx.patientId && (
                <Alert kind="error">
                  The unique patient ID was not passed from the dashboard, so Save is unavailable.
                  Reopen this service from the patient dashboard.
                </Alert>
              )}

              {fieldErrors._form && <Alert kind="error">{fieldErrors._form}</Alert>}

              <div>
                <h3 className="text-base font-semibold text-gray-800 mb-3">Body composition (BMR)</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{BMR_FIELDS.map(renderField)}</div>
              </div>

              <div>
                <h3 className="text-base font-semibold text-gray-800 mb-1">Additional clinical fields</h3>
                <p className="text-xs text-gray-500 mb-3">Entered manually; not read from the PDF. Leave blank if not tested.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{CLINICAL_FIELDS.map(renderField)}</div>
              </div>

              {saveStatus === "success" && <Alert kind="success">{saveMessage}</Alert>}
              {saveStatus === "partial" && (
                <Alert kind="warn">
                  {saveMessage}{" "}
                  <button type="button" onClick={handleRetryCompletion} className="underline font-medium">
                    Retry marking completed
                  </button>
                </Alert>
              )}
              {saveStatus === "error" && <Alert kind="error">Could not save: {saveMessage} Your values are kept — you can retry.</Alert>}

              <button
                type="button"
                onClick={handleSave}
                disabled={saving || redirecting || !ctx.patientId}
                className="bg-green-600 text-white px-5 py-2 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? "Saving…" : redirecting ? "Redirecting…" : saveStatus === "error" ? "Retry save" : "Save report"}
              </button>
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

export default VitalsWithPathologyForm;